import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Button, Table, Spinner } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import CompanyDashboardService from '../../../core/service/CompanyDashboardService';
import Utils from '../../../core/utilities/Utils';

function getPeriodBucketFromDataView(dataViewData, periodType, period) {
  if (!dataViewData || typeof dataViewData !== 'object') return null;
  if (periodType === 'YEARLY') {
    const yearData = dataViewData[period];
    if (!yearData || typeof yearData !== 'object') return null;
    return yearData.YEARLY || Object.values(yearData)[0];
  }
  const [year, periodKey] = String(period).split('-');
  const yearData = dataViewData[year];
  if (!yearData || typeof yearData !== 'object') return null;
  return yearData[periodKey];
}

const ESGAnalyticsDPBreakdownModal = ({ t, context, onHide }) => {
  const { title, period, periodType, branchId, requestPayload } = context || {};
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dataView, setDataView] = useState(null);

  useEffect(() => {
    if (!context?.requestPayload) {
      setDataView(null);
      setError('');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setDataView(null);

    CompanyDashboardService.getMetricDVAnalytics(context.requestPayload)
      .then((res) => {
        if (cancelled) return;
        if (res?.status === 200 && res.data) {
          setDataView(res.data);
        } else {
          setError(t('errorLoadingWidgets.label'));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(t('errorLoadingWidgets.label'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [context, t]);

  const rows = useMemo(() => {
    if (!dataView || !branchId) return [];
    const bucket = getPeriodBucketFromDataView(dataView, periodType, period);
    if (!bucket || !Array.isArray(bucket.datapoints)) return [];

    return bucket.datapoints
      .map((dp) => {
        const branch = (dp.branchConsolidated || []).find((b) => b.branchId === branchId);
        return { dp, branch };
      })
      .filter(({ branch }) => branch?.valueBeforeConversion === null)
      .map(({ dp, branch }) => {
        const name = [dp.dataPointCode, dp.dataPointName].filter(Boolean).join(' — ') || String(dp.dataPointName || dp.dataPointCode || '');
        return {
          key: `dp-${dp.dataPointId ?? name}`,
          name,
          value: null,
          uom: dp.uom || ''
        };
      })
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [dataView, periodType, period, branchId]);

  if (!context) {
    return null;
  }

  return (
    <Modal show onHide={onHide} size="lg" centered className="esg-analytics-dp-breakdown-modal">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="d-flex align-items-center gap-2 py-3 text-muted">
            <Spinner animation="border" size="sm" role="status" />
            <span>{t('Loading.label')}</span>
          </div>
        )}

        {!loading && error && (
          <div className="text-danger py-2">{error}</div>
        )}

        {!loading && !error && (
          <Table responsive bordered className="esg-analytics-dp-breakdown-table mb-0">
            <thead>
              <tr>
                <th>{t('Datapoint.label')}</th>
                <th className="text-end">{t('Value.label')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={2} className="text-center text-muted">
                    {t('ESGAnalytics.noDatapointsAvailable.label')}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const display = row.value != null && !isNaN(parseFloat(row.value))
                    ? Utils.formattedNumber(row.value)
                    : '-';
                  return (
                    <tr key={row.key}>
                      <td>{row.name}</td>
                      <td className="text-end">
                        {display}
                        {row.uom && row.value != null && display !== '-'
                          ? <span className="text-muted ms-1">({row.uom})</span>
                          : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('Close.label')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default withTranslation()(ESGAnalyticsDPBreakdownModal);
